// src/contracts/components/ContractTableEditor.js
// Editable table for contract sections: add/remove rows + columns, per-cell
// rich text (bold / italic / font size). Column widths use a relative weight
// (number) so the server-side PDF generator can mirror layout.
//
// State shape passed in via `value`:
//   { columns: [{ header: string, width: number }], rows: [[ string, ... ]] }
// onChange receives the updated table object.

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, ChevronDown, GripVertical } from 'lucide-react';

const SIZES = [
  { label: 'XS', cls: 'text-[10px]' },
  { label: 'S',  cls: 'text-[11px]' },
  { label: 'M',  cls: 'text-[12px]' },
  { label: 'L',  cls: 'text-[14px]' },
  { label: 'XL', cls: 'text-[16px]' },
];

function CellToolbar({ visible, onFormat }) {
  const [sizeOpen, setSizeOpen] = useState(false);
  if (!visible) return null;
  return (
    <div className="absolute -top-9 left-0 z-20 flex items-center gap-1 px-1 py-1 rounded-md bg-zinc-900 border border-zinc-700 shadow-xl text-zinc-200">
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); onFormat('bold'); }}
        className="px-2 py-1 hover:bg-zinc-800 rounded text-xs font-bold"
        aria-label="Bold"
      >B</button>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); onFormat('italic'); }}
        className="px-2 py-1 hover:bg-zinc-800 rounded text-xs italic"
        aria-label="Italic"
      >I</button>
      <div className="relative">
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); setSizeOpen(o => !o); }}
          className="px-2 py-1 hover:bg-zinc-800 rounded text-xs inline-flex items-center gap-1"
          aria-label="Font size"
        >
          A<ChevronDown className="w-3 h-3" />
        </button>
        {sizeOpen && (
          <div className="absolute top-7 left-0 bg-zinc-900 border border-zinc-700 rounded-md py-1 z-30 min-w-[60px]">
            {SIZES.map(s => (
              <button
                key={s.label}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onFormat('size', s.label); setSizeOpen(false); }}
                className={`block w-full text-left px-3 py-1 hover:bg-zinc-800 ${s.cls}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContractTableEditor({ value, onChange, compact = false }) {
  const safe = value || { columns: [], rows: [] };
  const [focusedCell, setFocusedCell] = useState(null); // { r: rowIdx | -1 for header, c: colIdx }
  const cellRefs = useRef({});

  // ---- Drag-to-reorder rows ----
  // Custom pointer drag: only the grip cell starts a drag (so selecting text in a
  // contentEditable cell never triggers a reorder). A floating copy of the row
  // follows the cursor and a dashed spacer marks the drop gap. State is local.
  // Row reorder: custom POINTER drag — a floating copy of the row follows the
  // cursor, a dashed spacer marks the drop gap, and the row lands in place with
  // no browser "snap-back". (Column-resize still uses dragRef, defined below.)
  const [drag, setDrag] = useState(null);           // { fromIndex, w, h, dx, dy } while dragging
  const [pointer, setPointer] = useState(null);     // { x, y } current cursor position
  const [dropIndex, setDropIndex] = useState(null); // insertion gap 0..rows.length
  const rowRefs = useRef([]);
  const rowDragRef = useRef(null);                  // mirror for the window pointerup handler

  // Sync DOM contents from state imperatively.
  //
  // contentEditable wants to own its DOM between focus and blur. Using
  // dangerouslySetInnerHTML re-applies state on every render and clobbers
  // anything the user has typed/deleted but not yet committed (state only
  // commits on blur). That's the bug behind "press Delete, text comes back
  // ~1s later" — an autosave re-render was wiping mid-edit DOM changes.
  //
  // Fix: render the cell with NO innerHTML in JSX, then sync DOM ← state in
  // a layout effect that runs every render but skips any cell currently
  // focused (so active edits are never touched).
  useLayoutEffect(() => {
    Object.entries(cellRefs.current).forEach(([key, el]) => {
      if (!el) return;
      if (document.activeElement === el) return; // never overwrite the focused cell
      const [r, c] = key.split(':').map(Number);
      const html = r === -1
        ? (safe.columns[c]?.header || '')
        : (safe.rows[r]?.[c] ?? '');
      if (el.innerHTML !== html) el.innerHTML = html;
    });
  });

  const totalWeight = safe.columns.reduce((s, c) => s + (Number(c.width) || 1), 0) || 1;

  const update = (next) => onChange?.(next);

  const setHeader = (c, html) => {
    const next = { ...safe, columns: safe.columns.map((col, i) => i === c ? { ...col, header: html } : col) };
    update(next);
  };
  const setCell = (r, c, html) => {
    const rows = safe.rows.map((row, i) =>
      i === r ? row.map((cell, j) => j === c ? html : cell) : row
    );
    update({ ...safe, rows });
  };

  const addRow = () => {
    const empty = safe.columns.map(() => '');
    update({ ...safe, rows: [...safe.rows, empty] });
  };
  const removeRow = (r) => {
    update({ ...safe, rows: safe.rows.filter((_, i) => i !== r) });
  };
  // Move row `from` into gap `insertIndex` (0..rows.length).
  const moveRowToIndex = (from, insertIndex) => {
    if (from == null || insertIndex == null) return;
    // Dropping into the gap directly above or below itself is a no-op.
    if (insertIndex === from || insertIndex === from + 1) return;
    const rows = [...safe.rows];
    const [moved] = rows.splice(from, 1);
    // After removal, gaps above `from` shift down by one.
    const dest = from < insertIndex ? insertIndex - 1 : insertIndex;
    rows.splice(dest, 0, moved);
    update({ ...safe, rows });
  };
  const computeDropIndex = (clientY, fromIndex) => {
    for (let i = 0; i < safe.rows.length; i++) {
      if (i === fromIndex) continue;
      const el = rowRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return i;
    }
    return safe.rows.length;
  };
  const startRowDrag = (e, index) => {
    e.preventDefault();
    const el = rowRefs.current[index];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    rowDragRef.current = { fromIndex: index, dropIndex: index };
    setDrag({ fromIndex: index, w: rect.width, h: rect.height, dx: e.clientX - rect.left, dy: e.clientY - rect.top });
    setPointer({ x: e.clientX, y: e.clientY });
    setDropIndex(index);
    const onMove = (ev) => {
      setPointer({ x: ev.clientX, y: ev.clientY });
      const idx = computeDropIndex(ev.clientY, index);
      rowDragRef.current.dropIndex = idx;
      setDropIndex(idx);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const d = rowDragRef.current;
      if (d) moveRowToIndex(d.fromIndex, d.dropIndex);
      rowDragRef.current = null;
      setDrag(null); setPointer(null); setDropIndex(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const addCol = () => {
    const cols = [...safe.columns, { header: 'New', width: 1 }];
    const rows = safe.rows.map(row => [...row, '']);
    update({ columns: cols, rows });
  };
  const removeCol = (c) => {
    if (safe.columns.length <= 1) return;
    const cols = safe.columns.filter((_, i) => i !== c);
    const rows = safe.rows.map(row => row.filter((_, i) => i !== c));
    update({ columns: cols, rows });
  };
  // ---- Drag-to-resize column widths (Google Sheets style) ----
  // The drag handle on column N's right edge "steals" width from column N+1,
  // so total table width stays at 100% — the on-screen table and the PDF
  // generator's table layout stay in lockstep (both use relative weights
  // summing to the same total). Min column width = 40px.
  const MIN_COL_PX = 40;
  const tableRef = useRef(null);
  const dragRef = useRef(null);

  const onColResizeStart = (colIdx, e) => {
    if (colIdx >= safe.columns.length - 1) return; // no neighbor to steal from
    e.preventDefault();
    e.stopPropagation();
    const tableW = tableRef.current ? tableRef.current.getBoundingClientRect().width : 600;
    // The "+ 36" col reserved at the end is the row-delete gutter — exclude it
    // from the resizable area so weights map cleanly.
    const usableW = Math.max(120, tableW - 36);
    const tw = safe.columns.reduce((s, c) => s + (Number(c.width) || 1), 0) || 1;
    dragRef.current = {
      colIdx,
      startX: e.clientX,
      startLeftW: Number(safe.columns[colIdx].width) || 1,
      startRightW: Number(safe.columns[colIdx + 1].width) || 1,
      pxPerWeight: usableW / tw,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const onColResizeMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dw = dx / d.pxPerWeight;
    // Min-width clamp expressed in weight units
    const minWeight = MIN_COL_PX / d.pxPerWeight;
    const totalPair = d.startLeftW + d.startRightW;
    let leftW  = d.startLeftW  + dw;
    let rightW = d.startRightW - dw;
    if (leftW  < minWeight) { leftW = minWeight;  rightW = totalPair - leftW; }
    if (rightW < minWeight) { rightW = minWeight; leftW  = totalPair - rightW; }
    const cols = safe.columns.map((col, i) =>
      i === d.colIdx     ? { ...col, width: leftW } :
      i === d.colIdx + 1 ? { ...col, width: rightW } :
      col
    );
    update({ ...safe, columns: cols });
  };

  const onColResizeEnd = (e) => {
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  };

  // --- Formatting (uses document.execCommand on the focused contentEditable) ---
  const applyFormat = (kind, arg) => {
    const el = focusedCell ? cellRefs.current[`${focusedCell.r}:${focusedCell.c}`] : null;
    if (!el) return;
    el.focus();
    if (kind === 'bold')   document.execCommand('bold', false, null);
    if (kind === 'italic') document.execCommand('italic', false, null);
    if (kind === 'size') {
      const map = { XS: 1, S: 2, M: 3, L: 5, XL: 6 }; // execCommand fontSize is 1..7
      document.execCommand('fontSize', false, String(map[arg] || 3));
    }
    // re-emit current contents so the model stays in sync
    const html = el.innerHTML;
    if (focusedCell.r === -1) setHeader(focusedCell.c, html);
    else setCell(focusedCell.r, focusedCell.c, html);
  };

  // Note: cell html is no longer set via JSX. The useLayoutEffect above is the
  // single source for DOM ← state syncing; the JSX renders an empty div.
  const renderCell = (r, c, _initialHtml, isHeader) => {
    const key = `${r}:${c}`;
    const focused = focusedCell && focusedCell.r === r && focusedCell.c === c;
    return (
      <div className="relative">
        {focused && <CellToolbar visible={true} onFormat={applyFormat} />}
        <div
          ref={el => { cellRefs.current[key] = el; }}
          contentEditable
          suppressContentEditableWarning
          onFocus={() => setFocusedCell({ r, c })}
          onBlur={(e) => {
            const html = e.currentTarget.innerHTML;
            if (isHeader) setHeader(c, html);
            else setCell(r, c, html);
          }}
          className={`px-2 py-1.5 outline-none focus:bg-indigo-950/40 ${isHeader ? 'text-zinc-300 font-medium text-[11px] uppercase tracking-wider' : 'text-zinc-100 text-[12px]'} ${compact ? 'min-h-[26px]' : 'min-h-[30px]'}`}
        />
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-zinc-800 bg-gray-950/40 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-950/60 border-b border-zinc-800">
        <button
          type="button"
          onClick={addRow}
          className="px-2 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800 rounded inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> row
        </button>
        <button
          type="button"
          onClick={addCol}
          className="px-2 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800 rounded inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> column
        </button>
        <div className="flex-1" />
        <span className="text-[10px] text-zinc-500">click a cell to format · drag column borders to resize · drag the grip to reorder rows</span>
      </div>

      <div className="overflow-x-auto">
        <table ref={tableRef} className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 26 }} />
            {safe.columns.map((c, i) => (
              <col key={i} style={{ width: `${(Number(c.width) || 1) / totalWeight * 100}%` }} />
            ))}
            <col style={{ width: 36 }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-950/70">
              <th className="border border-zinc-800" />
              {safe.columns.map((col, i) => (
                <th key={i} className="border border-zinc-800 align-top relative group/th">
                  {renderCell(-1, i, col.header || '', true)}
                  <div className="flex items-center justify-end px-1 pb-1">
                    <button
                      type="button"
                      onClick={() => removeCol(i)}
                      className="opacity-0 group-hover/th:opacity-100 transition-opacity text-zinc-500 hover:text-red-300 p-0.5"
                      aria-label="Delete column"
                      title="Delete column"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  {/* Drag handle on the right edge — invisible until hover.
                      Not rendered on the last column (no neighbor to steal from). */}
                  {i < safe.columns.length - 1 && (
                    <div
                      onPointerDown={(e) => onColResizeStart(i, e)}
                      onPointerMove={onColResizeMove}
                      onPointerUp={onColResizeEnd}
                      onPointerCancel={onColResizeEnd}
                      className="absolute top-0 right-0 h-full w-[6px] -mr-[3px] cursor-col-resize z-10 hover:bg-indigo-500/40 transition-colors"
                      role="separator"
                      aria-label={`Resize column ${i + 1}`}
                    />
                  )}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {safe.rows.flatMap((row, r) => {
              const isDragging = drag && drag.fromIndex === r;
              // A gap is meaningful only when it isn't the row's own slot.
              const gapHere = drag && dropIndex === r
                && dropIndex !== drag.fromIndex && dropIndex !== drag.fromIndex + 1;
              const gapAtEnd = drag && dropIndex === safe.rows.length
                && r === safe.rows.length - 1
                && dropIndex !== drag.fromIndex && dropIndex !== drag.fromIndex + 1;
              const spacer = (key) => (
                <tr key={key} className="pointer-events-none">
                  <td colSpan={safe.columns.length + 2} className="p-0">
                    <div className="h-9 m-1 rounded border-2 border-dashed border-indigo-400 bg-indigo-500/15" />
                  </td>
                </tr>
              );
              const els = [];
              if (gapHere) els.push(spacer(`gap-${r}`));
              els.push(
                <tr
                  key={r}
                  ref={(el) => (rowRefs.current[r] = el)}
                  className={`group/row ${isDragging ? 'hidden' : ''}`}
                >
                  <td
                    className="border border-zinc-800 align-middle text-center select-none"
                    style={{ touchAction: 'none' }}
                    onPointerDown={(e) => startRowDrag(e, r)}
                    title="Drag to reorder row"
                  >
                    <GripVertical className="w-3.5 h-3.5 mx-auto text-zinc-600 group-hover/row:text-zinc-300 cursor-grab active:cursor-grabbing" />
                  </td>
                  {safe.columns.map((_, c) => (
                    <td key={c} className="border border-zinc-800 align-top">
                      {renderCell(r, c, row[c] || '', false)}
                    </td>
                  ))}
                  <td className="border border-zinc-800 align-top text-center">
                    <button
                      type="button"
                      onClick={() => removeRow(r)}
                      className="text-zinc-500 hover:text-red-300 p-1.5"
                      aria-label="Delete row"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              );
              if (gapAtEnd) els.push(spacer('gap-end'));
              return els;
            })}
          </tbody>
        </table>
      </div>

      {/* Floating copy of the row that follows the cursor (portaled to body so nested
          scroll/overflow containers can't clip it). No native ghost = no snap-back. */}
      {drag && pointer && createPortal((() => {
        const row = safe.rows[drag.fromIndex] || [];
        return (
          <div
            className="fixed z-[1000] pointer-events-none rounded border border-indigo-400 bg-gray-900 shadow-2xl overflow-hidden"
            style={{ left: pointer.x - drag.dx, top: pointer.y - drag.dy, width: drag.w }}
          >
            <table className="w-full border-collapse text-left" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 26 }} />
                {safe.columns.map((c, i) => (
                  <col key={i} style={{ width: `${(Number(c.width) || 1) / totalWeight * 100}%` }} />
                ))}
                <col style={{ width: 36 }} />
              </colgroup>
              <tbody>
                <tr>
                  <td className="border border-zinc-800 align-middle text-center">
                    <GripVertical className="w-3.5 h-3.5 mx-auto text-zinc-400" />
                  </td>
                  {safe.columns.map((_, c) => (
                    <td key={c} className="border border-zinc-800 align-top">
                      <div
                        className={`px-2 py-1.5 text-zinc-100 text-[12px] ${compact ? 'min-h-[26px]' : 'min-h-[30px]'}`}
                        dangerouslySetInnerHTML={{ __html: row[c] || '' }}
                      />
                    </td>
                  ))}
                  <td className="border border-zinc-800" />
                </tr>
              </tbody>
            </table>
          </div>
        );
      })(), document.body)}
    </div>
  );
}
